import { c as _c } from "react/compiler-runtime";
import type { JiraInstallations } from '@/generated/prisma/browser';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { brokenIntegrationsQueryKey } from '@/data/integrations/use-broken-integrations';
import { refetchSelectedSharedPmConnection } from '@/data/nango/shared-pm-realtime';
import { useNangoIntegration } from '@/data/nango/use-nango-integration';
import { trackClientEvent } from '@/lib/analytics/posthog-client';
import { getIntegrationConnectionState } from '@/lib/integrations/auth-status';
import { ATLASSIAN_FULL_SCOPES } from '@/lib/nango/atlassian-scopes';
import { REALTIME_KEYS } from '@/lib/realtime/types';
export const jiraQueryKey = (installationId?: number) => ['jira-connection', installationId !== undefined ? String(installationId) : 'no-installation'];
export function useJiraIntegration(installationId) {
  const $ = _c(34);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== installationId) {
    t0 = jiraQueryKey(installationId);
    $[0] = installationId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const queryKey = t0;
  let t1;
  if ($[2] !== installationId) {
    t1 = async t2 => {
      const {
        useContextForReview,
        useContextForPrDescription,
        automaticallyAssociateIssues,
        showLinkedIssueSummary
      } = t2;
      const params = new URLSearchParams({
        useContextForReview: String(useContextForReview),
        useContextForPrDescription: String(useContextForPrDescription),
        automaticallyAssociateIssues: String(automaticallyAssociateIssues),
        showLinkedIssueSummary: String(showLinkedIssueSummary)
      });
      if (installationId !== undefined) {
        params.append("installationId", String(installationId));
      }
      const res = await fetch(`/api/jira/settings?${params.toString()}`, {
        method: "PATCH"
      });
      if (!res.ok) {
        throw new Error("Failed to update Jira settings");
      }
      return res.json();
    };
    $[2] = installationId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  let t3;
  let t4;
  if ($[4] !== queryClient || $[5] !== queryKey) {
    t2 = async variables => {
      await queryClient.cancelQueries({
        queryKey
      });
      const previous = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, old => {
        if (!old) {
          return old;
        }
        return {
          ...old,
          useContextForReview: variables.useContextForReview,
          useContextForPrDescription: variables.useContextForPrDescription,
          automaticallyAssociateIssues: variables.automaticallyAssociateIssues,
          showLinkedIssueSummary: variables.showLinkedIssueSummary
        };
      });
      return {
        previous
      };
    };
    t3 = (_err, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
      toast.error("Failed to update Jira settings");
    };
    t4 = data => {
      queryClient.setQueryData(queryKey, old_0 => {
        if (!old_0) {
          return old_0;
        }
        return {
          ...old_0,
          useContextForReview: data.settings.useContextForReview,
          useContextForPrDescription: data.settings.useContextForPrDescription,
          showLinkedIssueSummary: data.settings.showLinkedIssueSummary,
          ...(typeof data.settings.automaticallyAssociateIssues === "boolean" && {
            automaticallyAssociateIssues: data.settings.automaticallyAssociateIssues
          })
        };
      });
      toast.success("Jira settings updated");
    };
    $[4] = queryClient;
    $[5] = queryKey;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t2 = $[6];
    t3 = $[7];
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t1 || $[10] !== t2 || $[11] !== t3 || $[12] !== t4) {
    t5 = {
      mutationFn: t1,
      onMutate: t2,
      onError: t3,
      onSuccess: t4
    };
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const mutation = useMutation(t5);
  const t6 = installationId ? `/api/jira/connections/get?installationId=${installationId}` : "/api/jira/connections/get";
  const t7 = installationId ? `/api/jira/connections/delete?installationId=${installationId}` : "/api/jira/connections/delete";
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      authorization_params: {
        scope: ATLASSIAN_FULL_SCOPES
      }
    };
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const t9 = installationId !== undefined;
  let t10;
  if ($[15] !== installationId || $[16] !== queryClient) {
    t10 = {
      onConnectAttempt: _temp,
      onConnectSuccess: connection => {
        queryClient.invalidateQueries({
          queryKey: brokenIntegrationsQueryKey(installationId)
        });
        toast.dismiss("jira-refresh-failure");
        toast.success("Jira account connected");
        trackClientEvent("jira_connection_successful", undefined, {
          cloud_id: connection?.cloudId,
          base_url: connection?.baseUrl
        });
      },
      onConnectError: _temp2,
      onDisconnectSuccess: _temp3,
      onDisconnectError: _temp4,
      onPollingTimeout: _temp5
    };
    $[15] = installationId;
    $[16] = queryClient;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== installationId || $[19] !== queryKey || $[20] !== t10 || $[21] !== t6 || $[22] !== t7 || $[23] !== t9) {
    t11 = {
      provider: "jira",
      getConnectionUrl: t6,
      disconnectUrl: t7,
      realtimeEventKey: REALTIME_KEYS.JIRA_CONNECTION_UPDATE,
      queryKey,
      authOptions: t8,
      installationId,
      enabled: t9,
      getConnectionState: getIntegrationConnectionState,
      callbacks: t10,
      onRealtimeEventAction: _temp6
    };
    $[18] = installationId;
    $[19] = queryKey;
    $[20] = t10;
    $[21] = t6;
    $[22] = t7;
    $[23] = t9;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const nangoIntegration = useNangoIntegration(t11);
  let t12;
  if ($[25] !== queryClient || $[26] !== queryKey) {
    t12 = function getCurrentConnection() {
      return queryClient.getQueryData(queryKey) as JiraInstallations | undefined;
    };
    $[25] = queryClient;
    $[26] = queryKey;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const getCurrentConnection = t12;
  let t13;
  if ($[28] !== getCurrentConnection || $[29] !== mutation) {
    t13 = t14 => {
      const partial = t14 === undefined ? {} : t14;
      const current = getCurrentConnection();
      const merged = {
        useContextForReview: typeof partial.useContextForReview === "boolean" ? partial.useContextForReview : current?.useContextForReview ?? true,
        useContextForPrDescription: typeof partial.useContextForPrDescription === "boolean" ? partial.useContextForPrDescription : current?.useContextForPrDescription ?? true,
        automaticallyAssociateIssues: typeof partial.automaticallyAssociateIssues === "boolean" ? partial.automaticallyAssociateIssues : current?.automaticallyAssociateIssues ?? false,
        showLinkedIssueSummary: typeof partial.showLinkedIssueSummary === "boolean" ? partial.showLinkedIssueSummary : current?.showLinkedIssueSummary ?? true
      };
      mutation.mutate(merged);
    };
    $[28] = getCurrentConnection;
    $[29] = mutation;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== nangoIntegration || $[32] !== t13) {
    t14 = {
      ...nangoIntegration,
      updateJiraSettings: t13
    };
    $[31] = nangoIntegration;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  return t14;
}
function _temp6(payload, t0) {
  const {
    queryClient: queryClient_0,
    queryKey: queryKey_0,
    setIsPolling
  } = t0;
  if (payload?.refreshFailure) {
    refetchSelectedSharedPmConnection({
      queryClient: queryClient_0,
      queryKey: queryKey_0,
      setIsPolling
    });
    toast.error("Your Jira connection expired. Please reconnect.", {
      id: "jira-refresh-failure"
    });
    trackClientEvent("jira_connection_failed", undefined, {
      reason: "refresh_failure",
      connection_id: payload.refreshFailure.connectionId,
      error_type: payload.refreshFailure.errorType
    });
    return;
  }
  refetchSelectedSharedPmConnection({
    queryClient: queryClient_0,
    queryKey: queryKey_0,
    setIsPolling
  });
}
function _temp5(attempts) {
  trackClientEvent("jira_connection_failed", undefined, {
    reason: "polling_timeout",
    attempts
  });
  toast.error("Failed to confirm Jira connection. Please try again.");
}
function _temp4() {
  toast.error("Failed to disconnect Jira");
}
function _temp3() {
  toast.success("Jira account disconnected");
}
function _temp2(_error, reason) {
  if (reason === "popup_blocked") {
    toast.error("Browser blocked the Jira authentication popup. Please allow popups for this site and try again.");
    trackClientEvent("jira_connection_failed", undefined, {
      reason: "popup_blocked"
    });
  } else {
    toast.error("Failed to connect Jira");
    trackClientEvent("jira_connection_failed", undefined, {
      reason: reason || "auth_error"
    });
  }
}
function _temp() {
  trackClientEvent("jira_connection_attempt", undefined);
}